import { AuthError } from "@supabase/supabase-js";
import * as tus from "tus-js-client";
import AudioDb from "../classes/audioDb";
import { useState, useCallback, useMemo } from "react";
import { FileUploadingState, PatientRecord, TranscriptSection, TranscriptSectionStatus } from "../utils/types";
import { getDocumentTypesByIdsList, upsertTranscriptSection, supabase } from "../lib/supabaseClient";
import { holdForUpload } from "../lib/recordingLease";
import { persistTranscriptSectionFailureApi, transcribeAudio } from "../lib/api";
import { clearClientMetadata, getClientMetadata } from "../lib/recordingTelemetry";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { isFileSizeValid } from "./useAudioRecorder";
import { addOrUpdateRecord, updateTableRecord, updateTranscriptSectionStatus } from "@redux/patientTableRecordsSlice";
import { cloneDeep } from "lodash";
import { useRealtimeContextApi } from "@providers/RealtimeProvider";
import { AUDIO_STORAGE_BUCKET, VITE_PUBLIC_SUPABASE_URL } from "@common/utils/constants";
import { documentTypeRemoved, removeDocumentForDoctor } from "@/state/redux/documentSlice";
import * as Sentry from "@sentry/react";
import { finalizingSettled, uploadSettled, uploadStarted } from "@/state/redux/globalState";
import { useAccountContext } from "@/state/providers/AccountProvider";
import {
    getDocumentAssets,
    getPatientRecordId,
    getSelectedAssetIds,
    getTranscriptSections,
    updateOrAddTranscriptSection,
    addTranscriptSection,
} from "@/features/patient-view/state/redux/activePatientDataSlice";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { stopSentryReplay } from "../utils/helpers";
import { verifyAudioBlobIntegrity, buildDeadBlobSentryExtra } from "../lib/audioBlobIntegrity";

/**
 * @returns `uploadAudio`: The function to upload the audio file
 * @returns `uploadProgress`: The progress of the upload
 * @returns `uploadingState`: The state of the upload
 * @description A hook to handle the file upload process
 */
function useFileUpload() {
    // hooks
    const { selectedDocumentsRef, generateOnStopRef, recordingMonitoringDocumentIdRef } = useRealtimeContextApi();
    const { accountData } = useAccountContext();
    const { getIncompleteUploads } = useDataContextApi();
    const dispatch = useAppDispatch();
    const selectedAssetIds = useAppSelector(getSelectedAssetIds);
    const transcriptSectionIds = useAppSelector(getTranscriptSections).map((section) => section.id);
    const documentAssetIds = useAppSelector(getDocumentAssets).map((asset) => asset.id);
    const activePatientRecordId = useAppSelector(getPatientRecordId);

    // component state
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingState, setUploadingState] = useState<FileUploadingState>(FileUploadingState.inactive);
    const [uploadMessage, setUploadMessage] = useState<{ message: string; type: "info" | "error" | "warning" } | null>(
        null,
    );
    const [failedToUploadOrStoreRecording, setFailedToUploadOrStoreRecording] = useState<File | null>(null);

    // if we are not on patient view (there is no active patient id), we don't want to use the selected asset ids.
    // instead, we want to use all the assets (which we can achieve by setting the selected asset ids to undefined)
    const selectedTsIds = useMemo(() => {
        if (!activePatientRecordId) return undefined;
        return selectedAssetIds.filter((id) => transcriptSectionIds.includes(id));
    }, [selectedAssetIds, transcriptSectionIds, activePatientRecordId]);

    const selectedDocAssetIds = useMemo(() => {
        if (!activePatientRecordId) return undefined;
        return selectedAssetIds.filter((id) => documentAssetIds.includes(id));
    }, [selectedAssetIds, documentAssetIds, activePatientRecordId]);

    /**
     * @param `size`: The size of the file
     * @param `patientRecord`: The patient record to update
     * @description Handles the error that occured during the recording process
     * */
    const handleLargeFile = async (size: number, patientRecord: PatientRecord) => {
        if (!isFileSizeValid(size)) {
            setUploadMessage({ message: "File size too large", type: "error" });

            if (patientRecord?.id) {
                updateTableRecord({
                    id: patientRecord?.id,
                    updatedRecord: { id: patientRecord?.id },
                });
            }

            return { resp: null, err: "file size too large", is409: false };
        }

        return { resp: null, err: null, is409: false };
    };

    /**
     *
     * @param patientRecord
     * @param newTranscriptSection
     * @description Handles the error that occurred during the recording process
     */
    const handleError = useCallback(
        async (
            patientRecord: PatientRecord,
            newTranscriptSection: TranscriptSection,
            status: TranscriptSectionStatus,
            // Tri-state, same contract as uploadAudio's targetMonitoringDocumentId: omit
            // (`undefined`) to fall back to the live account context; pass a value (including
            // explicit `null`) to use it verbatim. uploadAudio passes its own resolved value through
            // on every call so a LocalStorageModal retry's persisted account isn't overridden here.
            explicitAccountId?: string | null,
        ) => {
            const resolvedAccountId = explicitAccountId !== undefined ? explicitAccountId : accountData?.id;
            setUploadMessage({
                message:
                    status === TranscriptSectionStatus.FailedIncompleteUpload
                        ? "Error while uploading audio. Retry."
                        : "Error processing audio. Retry.",
                type: "error",
            });

            newTranscriptSection = cloneDeep(newTranscriptSection);
            patientRecord = cloneDeep(patientRecord);

            newTranscriptSection.status = status;
            // "loading" in `data` keeps the clip UI in the indeterminate "processing" state even
            // after a failed `status` is set — same for active patient `transcriptSections` vs table.
            if (newTranscriptSection.data === "loading") {
                newTranscriptSection.data = "";
            }

            if (!patientRecord.transcriptions)
                patientRecord.transcriptions = { id: newTranscriptSection.transcription_id };

            if (!patientRecord?.transcriptions?.transcript_sections) {
                patientRecord.transcriptions.transcript_sections = [];
            }

            // push or update the new transcript section
            if (patientRecord?.transcriptions?.transcript_sections) {
                const index = patientRecord.transcriptions.transcript_sections.findIndex(
                    (section) => section.id === newTranscriptSection.id,
                );

                if (index !== -1) {
                    patientRecord.transcriptions.transcript_sections[index] = newTranscriptSection;
                } else {
                    patientRecord.transcriptions.transcript_sections.push(newTranscriptSection);
                }
            }

            dispatch(addOrUpdateRecord(patientRecord));
            // Keep patient view clips list in sync (isProcessing, TranscriptionLine, etc.)
            // Use addTranscriptSection: the failure-recovery path may run before the section
            // is in Redux (e.g. incomplete-upload before fetchPatientData has seeded it).
            dispatch(
                addTranscriptSection({
                    transcriptId: newTranscriptSection.transcription_id as string,
                    transcriptSection: newTranscriptSection,
                }),
            );

            if (status === TranscriptSectionStatus.FailedIncompleteUpload) {
                await getIncompleteUploads();
            }

            const { error: persistError } = await persistTranscriptSectionFailureApi({
                transcriptSectionId: newTranscriptSection.id,
                patientRecordId: patientRecord.id,
                status,
                transcriptionId: newTranscriptSection.transcription_id,
                accountId: resolvedAccountId,
                unprocessedFilename: newTranscriptSection.unprocessed_filename,
            });
            if (persistError) {
                Sentry.captureException(persistError, {
                    extra: {
                        context: "handleError/persistTranscriptSectionFailureApi",
                        transcriptSectionId: newTranscriptSection.id,
                        patientRecordId: patientRecord.id,
                        status,
                    },
                });
            }

            // Ensure the row exists and data is cleared (bug #1 & #3 fix): persistTranscriptSectionFailureApi
            // uses the mark-failed endpoint which may not upsert or clear data, so we follow up with an
            // explicit upsert to guarantee both the failure is persisted (even if the row didn't exist yet)
            // and the data field is cleared (preventing stuck "loading" state on reload).
            const { error: upsertError } = await upsertTranscriptSection({
                id: newTranscriptSection.id,
                transcription_id: newTranscriptSection.transcription_id,
                status,
                data: "",
                patient_record_id: patientRecord.id,
                unprocessed_filename: newTranscriptSection.unprocessed_filename,
                account_id: resolvedAccountId ?? undefined,
            });
            if (upsertError) {
                Sentry.captureException(upsertError, {
                    extra: {
                        context: "handleError/upsertTranscriptSection",
                        transcriptSectionId: newTranscriptSection.id,
                        patientRecordId: patientRecord.id,
                        status,
                    },
                });
            }

            return { resp: null, err: status, is409: false };
        },
        [accountData?.id, dispatch, getIncompleteUploads],
    );

    /**
     * @param `audioBlob`: The audio blob to be handled
     * @returns `void`
     * @description Handles the audio blob that is created after the recording is stopped
     */
    const handleAudioBlob = useCallback(
        async (
            audioFile: File,
            patientRecord: PatientRecord,
            newTranscriptSection: TranscriptSection,
            targetMonitoringDocumentId: string | null,
        ): Promise<void> => {
            // set up new record
            // check if session is valid
            if (!patientRecord || !newTranscriptSection) {
                Sentry.addBreadcrumb({
                    message: "newTranscriptSection or patient record not found",
                    level: "error",
                    data: {
                        accountId: accountData?.id,
                    },
                });
                return;
            }

            // check if we want to generate on stop
            let docIds: string[] = [];

            if (generateOnStopRef.current) {
                // get the selected documents
                docIds = selectedDocumentsRef.current;

                // look up the selected documents
                const documents = await getDocumentTypesByIdsList(docIds);

                for (const doc of documents) {
                    // check if the document is archived and if so, do not generate a new document and display a modal
                    if (doc?.is_archived) {
                        setUploadMessage({ message: "Document no longer exists.", type: "warning" });
                        // remove the document from the selected documents
                        if (doc.id) {
                            await dispatch(removeDocumentForDoctor({ documentTypeId: doc.id }) as any);
                        }

                        dispatch(documentTypeRemoved(doc?.id));
                        // remove the document from the selected documents
                        if (selectedDocumentsRef.current) {
                            selectedDocumentsRef.current = selectedDocumentsRef.current.filter(
                                (selectedId) => selectedId !== doc.id,
                            );
                        }

                        docIds = docIds.filter((docId: string) => docId !== doc.id);
                    }
                }
            }

            // if we are on patient view, we will use the selected asset ids plus the new transcript section id
            let docAssetsToUse = selectedDocAssetIds;
            let tsIdsToUse = selectedTsIds ? [...selectedTsIds, newTranscriptSection.id] : [newTranscriptSection.id];

            // if we are not on patient view, we have set the selected asset ids to undefined
            // thus, we will set the selected asset ids to an empty array to tell the server to use everything
            if (!selectedTsIds && !selectedDocAssetIds) {
                docAssetsToUse = [];
                tsIdsToUse = [];
            }

            // hit transcribe endpoint. Snapshot the session's metadata BEFORE the
            // call so the per-session Map entry can be cleared after — keying by
            // transcriptSectionId means each session reads its own snapshot
            // regardless of whether a newer recording has armed in the meantime
            // (browser hour rollover used to race the singleton).
            const sessionId = newTranscriptSection.id;
            const sessionMetadata = getClientMetadata(sessionId);
            const { error } = await transcribeAudio(
                patientRecord?.transcriptions?.id as string,
                newTranscriptSection.id,
                audioFile.name,
                patientRecord.id,
                patientRecord?.patient_name || "",
                patientRecord.doctors?.id || "",
                docIds,
                docAssetsToUse,
                tsIdsToUse,
                sessionMetadata,
                targetMonitoringDocumentId,
                // EN-1266: same value as the TUS objectName prefix (set on the section at creation).
                newTranscriptSection.account_id ?? null,
            );
            // Drop the session entry whether the call succeeded or not — a retry
            // will arm a fresh session via captureSessionEnvironment.
            clearClientMetadata(sessionId);

            if (error) throw new Error("Transcription failed");

            // Work will run async; the service sets in_progress in the DB. Update Redux so the
            // clip list and "Clip processing…" state match without waiting for a refetch.
            dispatch(
                updateOrAddTranscriptSection({
                    transcriptId: patientRecord?.transcriptions?.id as string,
                    transcriptSection: {
                        id: newTranscriptSection.id,
                        status: TranscriptSectionStatus.InProgress,
                        data: "loading",
                        unprocessed_filename: newTranscriptSection.unprocessed_filename,
                    },
                }),
            );
            // Also update the table record so it reflects InProgress (bug #2 fix)
            dispatch(
                updateTranscriptSectionStatus({
                    id: patientRecord.id,
                    sectionId: newTranscriptSection.id,
                    status: TranscriptSectionStatus.InProgress,
                }),
            );

            Sentry.addBreadcrumb({
                category: "uploadAudio",
                message: "Transcription completed",
                level: "info",
                data: { transcriptSectionId: newTranscriptSection?.id },
            });
        },
        [selectedTsIds, selectedDocAssetIds, selectedDocumentsRef, generateOnStopRef, dispatch, accountData?.id],
    );

    const refreshSession = async () => {
        const {
            data: { session },
            error: refreshError,
        } = await supabase.auth.refreshSession();
        if (refreshError) {
            Sentry.captureException(refreshError);
            setUploadMessage({ message: "Please reauthenticate.", type: "error" });
            stopSentryReplay();
            return null;
        }
        // Session refreshed, retry the operation
        return session?.access_token;
    };

    const uploadAudioUnleased = async (
        audioFile: File,
        audioName: string,
        transcriptId: string,
        transcriptSectionId: string,
        patientRecord: PatientRecord,
        // Tri-state: omit (`undefined`) to fall back to the live-recording ref below; pass a value
        // (including explicit `null`) to use it verbatim. Only the live-recording call site
        // (AssetLibraryProvider.onAudioDataAvailableOld), which runs immediately after
        // captureMonitoringDocumentIdForRecording froze this ref for THIS recording, may rely on
        // the fallback — every other caller (manual file upload, IndexedDB retry) must pass an
        // explicit value, since the ref reflects whatever unrelated recording last ran this session.
        targetMonitoringDocumentId?: string | null,
        // Tri-state, same contract as targetMonitoringDocumentId above: omit (`undefined`) to fall
        // back to the live account context; pass a value (including explicit `null`) to use it
        // verbatim. EN-1171: LocalStorageModal retries a recording possibly captured under a
        // different clinic account than whichever is live now, so it must pass the persisted
        // recording.accountId explicitly rather than let this fall back to the live account.
        explicitAccountId?: string | null,
    ): Promise<{ transcriptSection: TranscriptSection | null; err: Error | AuthError | any; is409: boolean }> => {
        // Read the target frozen at recording-start time (see AssetLibraryProvider's
        // captureMonitoringDocumentIdForRecording), not the live "whichever tab is open right
        // now" ref — the recording widget persists across tab navigation, so a patient/tab
        // switch either while still recording OR while this upload is in flight must not
        // retarget the clip away from the Monitoring document that was open when recording
        // actually began. Only used when the caller didn't pass an explicit target.
        const resolvedTargetMonitoringDocumentId =
            targetMonitoringDocumentId !== undefined
                ? targetMonitoringDocumentId
                : recordingMonitoringDocumentIdRef.current;
        const resolvedAccountId = explicitAccountId !== undefined ? explicitAccountId : accountData?.id;

        //Make a copy of patientRecord
        patientRecord = cloneDeep(patientRecord);

        setUploadingState(FileUploadingState.inactive);
        setUploadMessage(null);

        if (!tus.canStoreURLs) {
            Sentry.addBreadcrumb({
                category: "uploadAudio",
                message: "TUS cannot store urls",
                level: "error",
            });
        }

        if (!tus.isSupported) {
            Sentry.addBreadcrumb({
                category: "uploadAudio",
                message: "TUS is not supported",
                level: "error",
            });
        }

        const {
            data: { session },
            error,
        } = await supabase.auth.getSession();

        if (error) {
            Sentry.captureException(error);
            stopSentryReplay();
            return { transcriptSection: null, err: error, is409: false };
        }

        if (!session) {
            Sentry.captureMessage("Session not found - useFileUpload.tsx", {
                level: "error",
            });
            stopSentryReplay();
            return { transcriptSection: null, err: "no session", is409: false };
        }

        // The Storage object path below is keyed by the clinic account, not the uploader's own
        // login (EN-1216) -- refuse to upload rather than write to a "null/..." or
        // "undefined/..." path that would be permanently orphaned.
        if (!resolvedAccountId) {
            Sentry.captureException(new Error("uploadAudio called without a resolved account_id"), {
                extra: { transcriptId, transcriptSectionId, audioName },
            });
            stopSentryReplay();
            return { transcriptSection: null, err: "no resolved account id", is409: false };
        }

        const localAudioDb = AudioDb.getInstance();

        if (
            !patientRecord.transcriptions ||
            !patientRecord.transcriptions.id ||
            !patientRecord.id ||
            !transcriptSectionId ||
            !transcriptId ||
            !audioFile
        ) {
            Sentry.captureMessage("Invalid arguments record - useFileUpload.tsx", {
                extra: {
                    patientRecord,
                    transcriptSectionId,
                    transcriptId,
                    audioFile,
                },
                level: "error",
            });
            stopSentryReplay();
            return { transcriptSection: null, err: "invalid patient record", is409: false };
        }

        // check file size and update patient record status if necessary
        const isFileTooLarge = await handleLargeFile(audioFile.size, patientRecord);
        if (isFileTooLarge.err) {
            stopSentryReplay();
            return { transcriptSection: null, err: isFileTooLarge.err, is409: false };
        }

        // EN-1217: catches (a) a hollow blob that slipped past the persist-time check — WebKit's
        // reclaim isn't tied to any single call site, so a blob can die after persisting but before
        // this upload attempt — and (b) a user manually re-selecting an already-hollow file via the
        // picker (several of the historical bad objects were exactly this: a vet re-uploading a
        // downloaded rescue file that was itself already a husk).
        const integrity = await verifyAudioBlobIntegrity(audioFile);
        if (integrity.kind === "dead") {
            setUploadMessage({
                message: "This recording's audio was lost by the browser and can't be recovered.",
                type: "error",
            });
            Sentry.captureException(new Error("recording.dead_blob_detected_on_upload"), {
                tags: { errorCategory: "recording", errorId: "recording.dead_blob_detected_on_upload" },
                extra: buildDeadBlobSentryExtra(integrity, {
                    transcriptId,
                    transcriptSectionId,
                    claimedSize: audioFile.size,
                }),
            });
            stopSentryReplay();
            return {
                transcriptSection: null,
                err: "This recording's audio was lost by the browser and can't be recovered.",
                is409: false,
            };
        }

        // add chat completion if it doesn't exist
        if (!patientRecord.chat_completions) patientRecord.chat_completions = [];

        // add new transcript section
        const timeNow = new Date().toISOString();
        const newTranscriptSection = {
            transcription_id: patientRecord.transcriptions.id,
            data: "loading",
            created_at: timeNow,
            unprocessed_filename: audioFile.name,
            processed_filename: "",
            was_removed: false,
            id: transcriptSectionId,
            retried_automatically: false,
            status: TranscriptSectionStatus.NotStarted,
            patient_record_id: patientRecord.id,
            // Guaranteed non-empty by the resolvedAccountId guard above (EN-1216 PR review) --
            // without this, the in-memory row dispatched on upload success has no account_id,
            // so AudioPlayer/download/move fall back to the viewer's own session id even though
            // this exact recording just uploaded correctly.
            account_id: resolvedAccountId,
        } as TranscriptSection;

        const userId = session?.user.id;

        try {
            return await new Promise((resolve, reject) => {
                let shouldRetryAfterAuthRefresh = true;
                const upload = new tus.Upload(audioFile, {
                    endpoint: `${VITE_PUBLIC_SUPABASE_URL}/storage/v1/upload/resumable`,
                    retryDelays: [1000, 3000, 5000, 10000, 20000, 60000],
                    headers: {
                        "x-upsert": "true",
                    },
                    // Ensures each chunk uses the current session (e.g. after refreshSession in onShouldRetry).
                    onBeforeRequest: async (req) => {
                        // Reset auth retry flag at the start of each request (bug #2 fix) so stale auth failures
                        // from a prior attempt don't block retries after network errors.
                        shouldRetryAfterAuthRefresh = true;
                        const {
                            data: { session },
                        } = await supabase.auth.getSession();
                        if (session?.access_token) {
                            req.setHeader("Authorization", `Bearer ${session.access_token}`);
                        }
                    },
                    onAfterResponse: async (_req, res) => {
                        const status = res.getStatus();
                        if (status === 403 || status === 401) {
                            const refreshedToken = await refreshSession();
                            shouldRetryAfterAuthRefresh = Boolean(refreshedToken);
                        }
                    },
                    uploadDataDuringCreation: true,
                    removeFingerprintOnSuccess: true, // Important if you want to allow re-uploading the same file https://github.com/tus/tus-js-client/blob/main/docs/api.md#removefingerprintonsucces
                    metadata: {
                        bucketName: AUDIO_STORAGE_BUCKET,
                        // Keyed by the clinic account (EN-1216), not the uploader's own login --
                        // resolvedAccountId is guaranteed non-empty here by the guard above.
                        objectName: `${resolvedAccountId}/${transcriptId}/${audioName}`,
                        filename: audioName,
                        contentType: audioFile.type,
                        cacheControl: "3600", // 24 hours
                    },
                    uploadSize: audioFile.size,
                    chunkSize: 6 * 1024 * 1024, // NOTE: it must be set to 6MB (for now) do not change it
                    onError: async function (error) {
                        if (error.message.includes("already exists") || error.message.includes("409")) {
                            await upload.abort(true);
                            // The upload attempt has terminated (the file already exists in Storage).
                            // Clear the recorder-busy flag on THIS path too: the EN-837 watchdog never
                            // force-releases an upload flag, so a 409 that returned without clearing it
                            // would wedge every deferred teardown (sign-out, router swap, reload)
                            // indefinitely. No unpersisted audio is at risk here (it's already stored).
                            dispatch(uploadSettled(transcriptSectionId));
                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "409 error",
                                level: "info",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                },
                            });
                            resolve({ transcriptSection: newTranscriptSection, err: null, is409: true });
                            return;
                        }

                        // `handleError` is applied once in the `uploadAudio` catch so the promise always settles
                        // and UI/DB are not put in a failed+retrying race. Always reject in `finally` so
                        // callers clear loading state (e.g. FileUploadButton) even if a step here throws.
                        let localFailureHandled = false;
                        try {
                            dispatch(uploadSettled(transcriptSectionId));

                            const { error: localStorageError } = await localAudioDb.updateUploadState(
                                transcriptId,
                                transcriptSectionId,
                                upload.url || "",
                                0,
                                false,
                            );
                            if (localStorageError) {
                                setUploadingState(FileUploadingState.local_storage_error);
                                setUploadMessage({
                                    message: "Cannot upload or save the recording to your device.",
                                    type: "error",
                                });
                                setFailedToUploadOrStoreRecording(audioFile);
                                // Only suppress outer handleError when this local-storage failure path
                                // has already set the specific UI failure state/message.
                                localFailureHandled = true;
                            } else {
                                setUploadingState(FileUploadingState.inactive);
                            }

                            Sentry.captureException(error, {
                                extra: {
                                    patientRecord,
                                    newTranscriptSection,
                                },
                            });

                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "Error uploading audio",
                                level: "error",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                    audioName,
                                    transcriptId,
                                    userId,
                                },
                            });
                        } catch (onErrorException) {
                            dispatch(uploadSettled(transcriptSectionId));
                            Sentry.captureException(onErrorException, { extra: { from: "uploadAudio onError" } });
                        } finally {
                            reject({ resp: null, err: error, localFailureHandled });
                        }
                    },

                    onUploadUrlAvailable: async function () {
                        Sentry.addBreadcrumb({
                            category: "uploadAudio",
                            message: "Upload URL available",
                            level: "info",
                            data: {
                                transcriptSectionId: newTranscriptSection?.id,
                                patientRecordId: patientRecord?.id,
                                audioName,
                                transcriptId,
                                userId,
                            },
                        });

                        await localAudioDb.updateUploadState(transcriptId, transcriptSectionId, upload.url || "");
                    },

                    onProgress: async function (bytesUploaded, bytesTotal) {
                        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);

                        if (percentage !== "100.00") {
                            await localAudioDb.updateUploadState(
                                transcriptId,
                                transcriptSectionId,
                                upload.url || "",
                                bytesUploaded,
                                true,
                            );
                        }

                        setUploadProgress(Number(percentage));
                    },
                    onShouldRetry: function (err, _retryAttempt, _options) {
                        const status = err.originalResponse ? err.originalResponse.getStatus() : 0;
                        // Do not retry if the status is a 409.
                        if (status === 409) {
                            return false;
                        }

                        if (status === 403 || status === 401) {
                            // tus expects onShouldRetry to return a synchronous boolean.
                            // refreshSession runs in onAfterResponse and stores the outcome here.
                            return shouldRetryAfterAuthRefresh;
                        }

                        Sentry.addBreadcrumb({
                            category: "uploadAudio",
                            message: "Retrying upload",
                            level: "info",
                            data: {
                                transcriptSectionId: newTranscriptSection?.id,
                                patientRecordId: patientRecord?.id,
                                audioName,
                                transcriptId,
                                userId,
                            },
                        });

                        // For any other status code, we retry.
                        return true;
                    },

                    onSuccess: async function () {
                        // Keep the recorder "busy" (isUploadingGlobal === true) until the finally
                        // below — i.e. until AFTER the transcript_section row is linked
                        // (upsertTranscriptSection) AND transcription has been triggered
                        // (handleAudioBlob). The EN-837 recorder-idle guard releases deferred
                        // destructive teardowns (sign-out, version reload, router swap) the instant
                        // this flag flips false; clearing it before the clip is linked reopens the
                        // EN-860 gap that strands the section (empty unprocessed_filename + orphaned
                        // Storage audio + no /transcription/start). The flag is never force-released
                        // by the watchdog, so it MUST clear on every exit path — hence the finally.
                        try {
                            // Update the upload state in the local db
                            await localAudioDb.updateUploadState(
                                transcriptId,
                                transcriptSectionId,
                                upload.url || "",
                                audioFile.size,
                                false,
                            );

                            // set local uploading UI to inactive. The load-bearing Redux flag
                            // (isUploadingGlobal) is cleared in the finally below — AFTER the
                            // section is linked and transcription is triggered. See the block
                            // comment on onSuccess for why (EN-860 / EN-837 teardown gap).
                            setUploadingState(FileUploadingState.inactive);
                            setUploadProgress(0);

                            if (!patientRecord?.transcriptions?.transcript_sections) {
                                patientRecord!.transcriptions!.transcript_sections = [];
                            }
                            // push or update the new transcript section
                            if (patientRecord?.transcriptions?.transcript_sections) {
                                const index = patientRecord.transcriptions.transcript_sections.findIndex(
                                    (section) => section.id === newTranscriptSection.id,
                                );

                                if (index !== -1) {
                                    // Update existing section
                                    patientRecord.transcriptions.transcript_sections[index] = newTranscriptSection;
                                } else {
                                    // Push new section
                                    patientRecord.transcriptions.transcript_sections.push(newTranscriptSection);
                                }
                            }

                            // update patient record in redux
                            dispatch(addOrUpdateRecord(patientRecord));

                            // update active patient record in redux — explicit add after successful
                            // upload (section may not yet be in Redux from the initial fetch).
                            dispatch(
                                addTranscriptSection({
                                    transcriptId: newTranscriptSection.transcription_id as string,
                                    transcriptSection: newTranscriptSection,
                                }),
                            );

                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "Upload success",
                                level: "info",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                    audioName,
                                    transcriptId,
                                    userId,
                                },
                            });

                            // add transcription to db
                            await upsertTranscriptSection({
                                id: transcriptSectionId,
                                transcription_id: transcriptId,
                                status: TranscriptSectionStatus.Uploaded,
                                data: "",
                                unprocessed_filename: audioFile.name,
                                patient_record_id: patientRecord.id,
                                account_id: resolvedAccountId ?? undefined,
                            });

                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "Transcript section upserted",
                                level: "info",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                    audioName,
                                    transcriptId,
                                    userId,
                                },
                            });

                            // handle audio blob — throws on transcription failure
                            try {
                                await handleAudioBlob(
                                    audioFile,
                                    patientRecord,
                                    newTranscriptSection,
                                    resolvedTargetMonitoringDocumentId,
                                );
                            } catch (transcribeError) {
                                stopSentryReplay();
                                Sentry.captureException(transcribeError, {
                                    extra: { patientRecord, newTranscriptSection, transcriptSectionId },
                                });
                                await handleError(
                                    patientRecord,
                                    newTranscriptSection,
                                    TranscriptSectionStatus.FailedErrorProcessing,
                                    resolvedAccountId,
                                );
                                resolve({
                                    transcriptSection: newTranscriptSection,
                                    err: transcribeError,
                                    is409: false,
                                });
                                return;
                            }

                            // delete uploaded audio from indexedDB if running low on space
                            try {
                                await localAudioDb.handleUploadedTranscriptSectionAudio(
                                    transcriptId,
                                    transcriptSectionId,
                                );
                            } catch (cleanupError) {
                                Sentry.captureException(cleanupError, {
                                    extra: {
                                        patientRecord,
                                        newTranscriptSection,
                                        transcriptSectionId,
                                        context: "localAudioDb.handleUploadedTranscriptSectionAudio",
                                    },
                                });
                            }

                            // stop sentry replay
                            stopSentryReplay();

                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "Upload process complete",
                                level: "info",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                    audioName,
                                    transcriptId,
                                    userId,
                                },
                            });

                            resolve({ transcriptSection: newTranscriptSection, err: null, is409: false });
                        } catch (onSuccessException) {
                            Sentry.captureException(onSuccessException, {
                                extra: { patientRecord, newTranscriptSection, transcriptSectionId },
                            });
                            setUploadingState(FileUploadingState.inactive);
                            setUploadProgress(0);
                            stopSentryReplay();
                            // `onSuccess` runs only after TUS upload completes, so this is a post-upload
                            // processing/transcription-path failure (not an incomplete upload).
                            await handleError(
                                patientRecord,
                                newTranscriptSection,
                                TranscriptSectionStatus.FailedErrorProcessing,
                                resolvedAccountId,
                            );
                            resolve({ transcriptSection: newTranscriptSection, err: onSuccessException, is409: false });
                        } finally {
                            // Load-bearing: release the recorder only now, on EVERY exit path
                            // (success, transcribe-failure inner catch + return, outer catch). This
                            // runs after the section is linked and transcription is kicked off, and
                            // guarantees the flag can never leak true (which would wedge the recorder
                            // busy forever since the upload flag is never force-released).
                            dispatch(uploadSettled(transcriptSectionId));
                        }
                    },
                });

                // If `findPreviousUploads` or `start` throws, reject so the outer `catch` runs
                // (updates UI + `handleError`) and callers are not left awaiting forever.
                // Run startup in an async IIFE so the Promise executor stays synchronous,
                // avoiding the async-executor footgun; the catch below rejects on any throw.
                void (async () => {
                    try {
                        // Check if there are any previous uploads to continue.
                        const previousUploads = await upload.findPreviousUploads();

                        // Found previous uploads so we select the first one. If you are
                        if (previousUploads.length > 0) {
                            Sentry.addBreadcrumb({
                                category: "uploadAudio",
                                message: "Resuming previous upload",
                                level: "info",
                                data: {
                                    transcriptSectionId: newTranscriptSection?.id,
                                    patientRecordId: patientRecord?.id,
                                    audioName,
                                    transcriptId,
                                    userId,
                                    previousUploads,
                                },
                            });

                            upload.resumeFromPreviousUpload(previousUploads[0]);
                        }
                        // Start the upload
                        upload.start();
                        setUploadingState(FileUploadingState.uploading);
                        dispatch(uploadStarted(transcriptSectionId));
                        // EN-837: the upload now owns the "recorder busy" claim, so release the
                        // `finalizing` bridge set at stop (keyed by this section id). Order matters —
                        // clear it AFTER uploadStarted() so the busy-state stays continuous across the
                        // handoff (never a tick where both are false). No-op for non-stop uploads, where
                        // finalizing was never armed for this key.
                        dispatch(finalizingSettled(transcriptSectionId));

                        Sentry.addBreadcrumb({
                            category: "uploadAudio",
                            message: "Upload started",
                            level: "info",
                            data: {
                                transcriptSectionId: newTranscriptSection?.id,
                                patientRecordId: patientRecord?.id,
                                audioName,
                                transcriptId,
                                userId,
                            },
                        });
                    } catch (startUploadException) {
                        Sentry.captureException(startUploadException, {
                            extra: { patientRecord, newTranscriptSection, transcriptId, transcriptSectionId },
                        });
                        dispatch(uploadSettled(transcriptSectionId));
                        setUploadingState(FileUploadingState.inactive);
                        setUploadProgress(0);
                        reject(startUploadException);
                    }
                })();
            });
        } catch (err: any) {
            // Unwrap the actual error for Sentry (bug #2 fix)
            const actualError = err?.err || err;
            Sentry.captureException(actualError, {
                extra: {
                    patientRecord,
                    newTranscriptSection,
                },
            });

            const hasLocalFailureBeenHandled = Boolean(err?.localFailureHandled);
            if (!hasLocalFailureBeenHandled) {
                const { error: localStorageError } = await localAudioDb.updateUploadState(
                    transcriptId,
                    transcriptSectionId,
                    "",
                    0,
                    false,
                );
                if (localStorageError) {
                    setUploadingState(FileUploadingState.local_storage_error);
                    setUploadMessage({ message: "Cannot upload or save the recording to your device.", type: "error" });
                    setFailedToUploadOrStoreRecording(audioFile);
                } else {
                    setUploadingState(FileUploadingState.inactive);
                }
            }
            dispatch(uploadSettled(transcriptSectionId));
            setUploadProgress(0);
            //Still add the record
            dispatch(addOrUpdateRecord(patientRecord));

            // Upload never finished (TUS onError, start/resume failure). Not a transcription failure —
            // FailedErrorProcessing would route retry to re-transcribe with no stored file.
            // Skip calling handleError if local failure was already handled to preserve the specific error message (bug #1 fix)
            const errObj = hasLocalFailureBeenHandled
                ? { resp: null, err: TranscriptSectionStatus.FailedIncompleteUpload, is409: false }
                : await handleError(
                      patientRecord,
                      newTranscriptSection,
                      TranscriptSectionStatus.FailedIncompleteUpload,
                      resolvedAccountId,
                  );

            Sentry.addBreadcrumb({
                category: "uploadAudio",
                message: "Error uploading audio - catch block",
                level: "error",
                data: {
                    transcriptSectionId: newTranscriptSection?.id,
                    patientRecordId: patientRecord?.id,
                    audioName,
                    transcriptId,
                    userId,
                },
            });

            // stop sentry replay
            stopSentryReplay();

            return { transcriptSection: null, err: errObj.err, is409: false };
        }
    };

    // EN-1088: the visit stays leased until this upload ends, i.e. until its clip row exists (or the
    // upload failed). Best-effort and never awaited: see recordingLease.ts.
    const uploadAudio: typeof uploadAudioUnleased = async (...args) => {
        const release = holdForUpload(args[4]?.id);
        try {
            return await uploadAudioUnleased(...args);
        } finally {
            release();
        }
    };

    return { uploadAudio, uploadProgress, uploadingState, uploadMessage, failedToUploadOrStoreRecording };
}

export default useFileUpload;
